// ────────────────────────────────────────────────────────────────────
// THE RAW-FILE MODAL — design_handoff/README.md:208-217.
//
// Opened by `OPEN RAW ▸` on the FILES HELD AGAINST IT cards. An absolute
// overlay anchored to the two-column row, so the CALLER'S row must carry
// `position: relative`; this component does not fix itself to the viewport,
// because the handoff draws it over the record it belongs to and not over the
// whole application.
//
// WHY THE MODAL EXISTS AT ALL. Every body type closes with a sentence saying
// why the raw file is openable — `WHY_OPENABLE` below. That sentence is the
// point of the screen, not decoration: it is the same argument the interval
// candidates make. A thickness on the rate strip is a claim, and the file it
// was read out of is the evidence. A number whose source cannot be opened is
// a number a reader has to take on trust.
//
// WHAT THIS COMPONENT DOES NOT DO:
//
//   • IT DOES NOT DRAW A SCAN GRID. The handoff's SCAN body is a 32 × 12 map
//     of a wall-loss ramp. No scan-grid data source exists anywhere in the
//     tree — no importer reads one, no table holds one, no endpoint returns
//     one. Drawing it would mean inventing measurements, so SCAN renders the
//     same honest empty state the rest of this screen uses (./emptyStates).
//     The spec's Open section rules this explicitly.
//   • IT DERIVES NO RATE, NO INTERVAL AND NO DUE DATE. The one comparison it
//     makes is per row and per file: a reading at or below THAT ROW'S OWN
//     t-min is flagged REVIEW. That is a display of two numbers already in
//     the file, not a corrosion calculation — nothing here halves a remaining
//     life or resolves which rate governs. See ./types.ts.
//   • IT INVENTS NO COLUMN. The CSV body reads the shape
//     `GET /api/intake/{id}/inspect` actually returns (server/routers/
//     intake.py:684 → lib/types.IntakeInspect): the FILE'S OWN headers, a
//     sample of rows, and a total. The handoff's seven columns are applied as
//     WIDTHS against the operator's import mapping, and where the mapping
//     does not name a column the modal says so rather than guessing which
//     header means "reading".
//
// AN UNSIGNED DRAFT'S FILES ARE NOT DOWNLOADABLE — README.md:325. The block
// here is real but it is only the UI half: `GET /api/intake/{id}/download`
// (server/routers/intake.py:667) gates on the tenant read roles and RLS and
// looks at no sign-off state whatever, and `IntakeFile` carries no link to a
// review or an analysis for it to look at. Anyone who can read the tenant can
// fetch the bytes by id. This component honours the rule on screen; it must
// not be described as enforcing it.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import {
  FILE_PROPERTIES_NOT_READ, PHOTO_FRAMES_NOT_CAPTURED, SCAN_GRID_NOT_CAPTURED,
} from "./emptyStates";
import { EMDASH } from "./format";

/** The handoff's six body types (README.md:210). */
export type RawFileKind = "CSV" | "SCAN" | "PDF" | "DIFF" | "PHOTOS" | "DOC";

/** The columns the handoff's CSV body names. A role is a claim about one of
 *  the FILE'S OWN headers, and it comes from the operator's import mapping
 *  (`schemas.ImportPreviewIn` — cml_no / thickness / t_min …), never from
 *  pattern-matching a header string here. */
export type CsvRole = "cml" | "component" | "method" | "t_min" | "reading" | "loss";

/** `GET /api/intake/{id}/inspect`, field-for-field (lib/types.IntakeInspect).
 *  `rows` is the endpoint's `sample` — the first rows, not the file. */
export interface RawCsvPreview {
  columns: string[];
  rows: ReadonlyArray<Record<string, string>>;
  total_rows: number;
  roles?: Partial<Record<CsvRole, string>>;
}

/** One placeholder in the PHOTOS grid, labelled with its grid reference.
 *  Supplied by whoever holds the file store — see PHOTO_FRAMES_NOT_CAPTURED. */
export interface RawPhotoFrame {
  ref: string;
  caption?: string;
}

/** One key/value row of the PDF / DIFF / DOC body, read off the intake row. */
export interface RawDetail {
  label: string;
  value: string;
}

/** The file the modal is open on. `signer` is the engineer who signed the
 *  report this file was filed against, read off `InspectionEvent.signer`;
 *  empty means an unsigned draft. */
export interface RawFileRef {
  id: number;
  filename: string;
  kind: RawFileKind;
  assetTag: string;
  reportRef: string;
}

// ── the closing sentences ───────────────────────────────────────────

/** WHY THE RAW FILE IS OPENABLE AT ALL — one sentence per body type, printed
 *  verbatim at the foot of every body (README.md:217).
 *
 *  Each is distinct on purpose. A single shared line would still render if
 *  five of the six bodies forgot to print one, and the test that walks these
 *  would pass on a screen that had lost the argument for five file types. */
export const WHY_OPENABLE: Record<RawFileKind, string> = {
  CSV:
    "This file is openable because the thickness on the rate strip was read out of it. "
    + "The report states a number; this is the row it came from, held as it arrived rather "
    + "than as the report re-typed it, so the reading and the minimum it was judged against "
    + "can be checked by the next engineer without asking anyone for the original.",
  SCAN:
    "This file is openable because a scan is the measurement and the report is a summary of "
    + "it. The summary can be re-read years later only if the thing it summarised is still "
    + "here, which is why the raw file outlives the report it was written from.",
  PDF:
    "This file is openable because the signed report is the record, and a record nobody can "
    + "open is an assertion. What is printed here is what the file store holds against this "
    + "inspection — enough to tell whether the document on screen is the document that was "
    + "signed.",
  DIFF:
    "This file is openable because a revision is only meaningful beside what it replaced. "
    + "The chain is kept on both sides — what this replaced and what replaced it — so an "
    + "assessment stays readable against the numbers it was actually judged on.",
  PHOTOS:
    "These are openable because a finding written as one line of prose is a description, and "
    + "the photograph is the observation. An engineer disputing a disposition years from now "
    + "needs the frame the inspector was looking at, not the sentence they wrote about it.",
  DOC:
    "This file is openable because a baseline taken from a document has to be traceable to "
    + "that document. A corrosion rate needs a starting point, and a starting point read off "
    + "a manufacturer's data report is only as good as the reader's ability to open it.",
};

/** Shown in place of a download control on an unsigned draft (README.md:325). */
export const UNSIGNED_NOT_DOWNLOADABLE =
  "CONDITIONS · UNSIGNED — no engineer has signed the report this file was filed against, "
  + "so the raw file is not offered for download. A draft's evidence leaving the system ahead "
  + "of the conclusion drawn from it is how an unsigned number ends up quoted as a finding.";

// ── layout ──────────────────────────────────────────────────────────

const MICRO: CSSProperties = {
  fontSize: 8.5, fontWeight: 500, letterSpacing: ".14em", color: "var(--ink-4)",
};

/** Handoff column widths (README.md:212-213). `component` is the flex column
 *  and carries no fixed width. */
const CSV_WIDTH: Partial<Record<CsvRole, number>> = {
  cml: 74, method: 48, t_min: 52, reading: 58, loss: 52,
};
const FLAG_WIDTH = 56;

const TH: CSSProperties = {
  fontSize: 8.5, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase",
  textAlign: "left", padding: "7px 8px", whiteSpace: "nowrap",
  // The handoff's muted header label on the near-black bar. --hairline is the
  // palette's warm light grey; no token sits at its #c9c2b6 exactly.
  color: "var(--hairline)",
};

const TD: CSSProperties = {
  fontSize: 11, padding: "6px 8px", borderBottom: "1px solid var(--line-3)",
  color: "var(--ink-2)", whiteSpace: "nowrap",
};

// ── CSV ─────────────────────────────────────────────────────────────

/** Which role, if any, the operator's mapping gave this header. */
function roleOf(column: string, roles: RawCsvPreview["roles"]): CsvRole | null {
  if (!roles) return null;
  for (const [role, mapped] of Object.entries(roles)) {
    if (mapped === column) return role as CsvRole;
  }
  return null;
}

/** REVIEW when the row's reading is AT OR BELOW the row's OWN t-min.
 *
 *  `<=`, not `<`: a reading exactly on the minimum is not a passing reading —
 *  README.md:322 puts an asset at or below t-min on hold. `null` means the
 *  question cannot be asked of this row (no mapping, or a cell that is not a
 *  number), which is a third state and never a silent "fine".
 *
 *  No tolerance is applied. TOL = 0.0005 exists for comparing two RATES, where
 *  float noise separates two gauges that agreed; a thickness against its own
 *  stated minimum is a direct comparison of two numbers off the same file. */
function reviewFlag(row: Record<string, string>, roles: RawCsvPreview["roles"]): boolean | null {
  const readingCol = roles?.reading;
  const tMinCol = roles?.t_min;
  if (!readingCol || !tMinCol) return null;
  const reading = Number(row[readingCol]);
  const tMin = Number(row[tMinCol]);
  if (!Number.isFinite(reading) || !Number.isFinite(tMin)) return null;
  return reading <= tMin;
}

function CsvBody({ csv }: { csv: RawCsvPreview }): JSX.Element {
  const flaggable = Boolean(csv.roles?.reading && csv.roles?.t_min);
  return (
    <div data-testid="raw-body-csv">
      <div style={{ overflowX: "auto", border: "1px solid var(--line-2)", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} className="font-mono">
          <thead>
            <tr style={{ background: "var(--ink-1)" }}>
              {csv.columns.map((c) => {
                const role = roleOf(c, csv.roles);
                const width = role ? CSV_WIDTH[role] : undefined;
                return (
                  <th key={c} data-testid="csv-header" data-role={role ?? ""}
                      style={{ ...TH, width }}>
                    {c}
                  </th>
                );
              })}
              {flaggable && <th style={{ ...TH, width: FLAG_WIDTH }}>flag</th>}
            </tr>
          </thead>
          <tbody>
            {csv.rows.map((row, i) => {
              const flag = reviewFlag(row, csv.roles);
              return (
                <tr key={i} data-testid="csv-row"
                    data-flag={flag == null ? "unknown" : flag ? "review" : "ok"}
                    style={{ background: flag ? "var(--surface-alert)" : undefined }}>
                  {csv.columns.map((c) => (
                    <td key={c} style={TD}>{row[c] ?? EMDASH}</td>
                  ))}
                  {flaggable && (
                    <td data-testid="csv-flag"
                        style={{
                          ...TD, fontWeight: 600, letterSpacing: ".06em",
                          color: flag ? "var(--err)" : "var(--ink-4)",
                        }}>
                      {flag ? "REVIEW" : EMDASH}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div data-testid="csv-sample-note" className="font-mono"
           style={{ fontSize: 9.5, color: "var(--ink-4)", marginTop: 6 }}>
        {csv.rows.length} of {csv.total_rows} rows — the inspect endpoint returns a sample of the
        file, not the file
      </div>

      {!flaggable && (
        // Never silently unflagged. Without a mapping naming the reading and
        // the minimum, guessing which header means which is how a column
        // called "t nom" gets read as a limit.
        <div data-testid="csv-no-mapping" className="font-mono"
             style={{ fontSize: 9.5, color: "var(--warn)", marginTop: 4 }}>
          no import mapping names which column is the reading and which is the minimum, so no row
          here is flagged
        </div>
      )}
    </div>
  );
}

// ── PHOTOS ──────────────────────────────────────────────────────────

function PhotosBody({ photos }: { photos: readonly RawPhotoFrame[] }): JSX.Element {
  if (photos.length === 0) {
    return (
      <div data-testid="photos-empty"
           style={{ fontSize: 11.5, lineHeight: 1.6, color: "var(--ink-4)" }}>
        {PHOTO_FRAMES_NOT_CAPTURED}
      </div>
    );
  }
  return (
    <div data-testid="raw-body-photos"
         style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
      {photos.map((p, i) => (
        <figure key={`${p.ref}-${i}`} data-testid="photo-frame" style={{ margin: 0 }}>
          <div style={{
            aspectRatio: "4 / 3", borderRadius: 6, border: "1px solid var(--line-2)",
            background:
              "repeating-linear-gradient(45deg, var(--line-2) 0 6px, var(--surface-2) 6px 12px)",
          }} />
          <figcaption className="font-mono"
                      style={{ fontSize: 9.5, color: "var(--ink-3)", marginTop: 4 }}>
            {p.ref}
            {p.caption && (
              <span style={{ color: "var(--ink-4)" }}> · {p.caption}</span>
            )}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

// ── PDF / DIFF / DOC ────────────────────────────────────────────────

function KeyValueBody({ details }: { details: readonly RawDetail[] }): JSX.Element {
  if (details.length === 0) {
    return (
      <div data-testid="details-empty"
           style={{ fontSize: 11.5, lineHeight: 1.6, color: "var(--ink-4)" }}>
        {FILE_PROPERTIES_NOT_READ}
      </div>
    );
  }
  return (
    <div data-testid="raw-body-keyvalue">
      {details.map((d, i) => (
        <div key={`${d.label}-${i}`} data-testid="detail-row"
             style={{
               display: "flex", alignItems: "flex-start", gap: 12,
               padding: "7px 0", borderBottom: "1px solid var(--line-3)",
             }}>
          <span className="font-mono"
                style={{
                  width: 150, flex: "none", fontSize: 9, fontWeight: 500,
                  letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink-4)",
                }}>
            {d.label}
          </span>
          <span style={{ fontSize: 12, lineHeight: 1.45, color: "var(--ink-1)", minWidth: 0 }}>
            {d.value || EMDASH}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── SCAN ────────────────────────────────────────────────────────────

/** No grid. See the header of this file and ./emptyStates. */
function ScanBody(): JSX.Element {
  return (
    <div data-testid="scan-empty"
         style={{ fontSize: 11.5, lineHeight: 1.6, color: "var(--ink-4)" }}>
      {SCAN_GRID_NOT_CAPTURED}
    </div>
  );
}

// ── the modal ───────────────────────────────────────────────────────

export function RawFileModal({
  file, signer, csv, photos = [], details = [], onClose, onDownload,
}: {
  file: RawFileRef;
  /** `InspectionEvent.signer` for the inspection this file is held against.
   *  Empty = an unsigned draft, whose files are not downloadable. */
  signer: string;
  csv?: RawCsvPreview;
  photos?: readonly RawPhotoFrame[];
  details?: readonly RawDetail[];
  onClose: () => void;
  onDownload?: (fileId: number) => void;
}): JSX.Element {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Focus goes to CLOSE on open and BACK TO THE TRIGGER on close. Without the
  // restore, dismissing the modal drops the caret at the top of the document
  // and a keyboard reader loses the card they opened.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => { previous?.focus?.(); };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const signed = signer.trim() !== "";
  const titleId = `raw-file-title-${file.id}`;

  return (
    <div data-testid="raw-file-scrim"
         onClick={onClose}
         style={{
           position: "absolute", inset: 0, zIndex: 40, padding: 30,
           display: "flex", alignItems: "flex-start", justifyContent: "center",
           overflow: "auto",
           // rgba(23,21,15,.42) is --ink at 42%. `in srgb` is not optional:
           // a color-mix() without it is invalid CSS that jsdom accepts and
           // browsers drop, and that exact bug shipped on this repo once.
           background: "color-mix(in srgb, var(--ink) 42%, transparent)",
         }}>
      <div data-testid="raw-file-modal"
           role="dialog" aria-modal="true" aria-labelledby={titleId}
           onClick={(e) => e.stopPropagation()}
           style={{
             width: "100%", maxWidth: 760, borderRadius: 12,
             border: "1px solid var(--hairline)", background: "var(--paper-2)",
             boxShadow: "0 24px 60px color-mix(in srgb, var(--ink) 28%, transparent)",
           }}>

        {/* header */}
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 12, padding: "13px 16px",
          borderBottom: "1px solid var(--line-2)",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span id={titleId} data-testid="raw-file-name" className="font-mono"
                    style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)" }}>
                {file.filename}
              </span>
              <span data-testid="raw-file-badge" className="font-mono"
                    style={{
                      fontSize: 8.5, fontWeight: 600, letterSpacing: ".12em", flex: "none",
                      padding: "3px 7px", borderRadius: 4,
                      background: "var(--ink-1)", color: "var(--paper-2)",
                    }}>
                {file.kind}
              </span>
            </div>
            <div data-testid="raw-file-origin" className="font-mono"
                 style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 3 }}>
              {file.assetTag || EMDASH} · {file.reportRef || EMDASH} · {file.kind}
            </div>
          </div>
          <button ref={closeRef} type="button" data-testid="raw-file-close" onClick={onClose}
                  className="font-mono"
                  style={{
                    flex: "none", fontSize: 9, fontWeight: 600, letterSpacing: ".12em",
                    padding: "5px 9px", borderRadius: 5, cursor: "pointer",
                    border: "1px solid var(--hairline)",
                    background: "var(--surface-2)", color: "var(--ink-2)",
                  }}>
            CLOSE ✕
          </button>
        </div>

        {/* body */}
        <div style={{ padding: "14px 16px" }}>
          {file.kind === "CSV" && (
            csv
              ? <CsvBody csv={csv} />
              : (
                <div data-testid="csv-empty" className="font-mono"
                     style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
                  the file has not been read yet — no rows arrived from the inspect endpoint
                </div>
              )
          )}
          {file.kind === "SCAN" && <ScanBody />}
          {file.kind === "PHOTOS" && <PhotosBody photos={photos} />}
          {(file.kind === "PDF" || file.kind === "DIFF" || file.kind === "DOC")
            && <KeyValueBody details={details} />}

          {/* the closing sentence — the reason the modal exists */}
          <p data-testid="raw-why"
             style={{
               fontSize: 11.5, lineHeight: 1.65, color: "var(--ink-3)",
               marginTop: 14, paddingTop: 11, borderTop: "1px solid var(--line-2)",
             }}>
            {WHY_OPENABLE[file.kind]}
          </p>
        </div>

        {/* download, or the reason there is none */}
        <div style={{
          padding: "11px 16px", borderTop: "1px solid var(--line-2)",
          background: signed ? "var(--surface-2)" : "var(--surface-warm-3)",
        }}>
          {signed && onDownload ? (
            <button type="button" data-testid="raw-download"
                    onClick={() => onDownload(file.id)} className="font-mono"
                    style={{
                      fontSize: 9, fontWeight: 600, letterSpacing: ".12em",
                      padding: "6px 11px", borderRadius: 5, cursor: "pointer",
                      border: "1px solid var(--hairline)",
                      background: "var(--paper-2)", color: "var(--accent-deep)",
                    }}>
              DOWNLOAD ▸
            </button>
          ) : signed ? (
            <span data-testid="raw-download-unavailable" className="font-mono"
                  style={{ ...MICRO }}>
              NO DOWNLOAD HANDLER WAS SUPPLIED
            </span>
          ) : (
            <div data-testid="raw-download-blocked"
                 style={{ fontSize: 11, lineHeight: 1.6, color: "var(--warn)" }}>
              {UNSIGNED_NOT_DOWNLOADABLE}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
